import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtEuro } from "@/lib/cart";
import {
  Loader2, Package, ChevronLeft, ShoppingBag,
  Receipt, Truck, CheckCircle2, Clock, X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_META = {
  received:  { label: "Eingegangen",  cls: "bg-amber-50 text-amber-700 border-amber-200",         Icon: Receipt },
  preparing: { label: "In Bearbeitung", cls: "bg-cyan-50 text-cyan-700 border-cyan-200",          Icon: Package },
  shipped:   { label: "Versendet",    cls: "bg-violet-50 text-violet-700 border-violet-200",     Icon: Truck },
  delivered: { label: "Zugestellt",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200",  Icon: CheckCircle2 },
  cancelled: { label: "Storniert",    cls: "bg-rose-50 text-rose-700 border-rose-200",           Icon: X },
};

export default function ShopOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shop/orders/me")
      .then(({ data }) => setOrders(data.items || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout title="Meine Bestellungen" subtitle="Bestellungen aus dem Easy Gutachter Shop">
      <div className="max-w-4xl">
        <Link to="/shop" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-4" data-testid="orders-back">
          <ChevronLeft className="h-4 w-4" /> Zurück zum Shop
        </Link>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
        ) : orders.length === 0 ? (
          <Card className="p-12 text-center" data-testid="orders-empty">
            <div className="h-14 w-14 rounded-2xl brand-gradient-bg flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="h-6 w-6 text-white" />
            </div>
            <div className="font-display text-lg font-bold">Noch keine Bestellungen</div>
            <p className="text-sm text-zinc-500 mt-1 mb-5">Stöbere durch unseren Shop und finde Werbematerial für deinen Auftritt.</p>
            <Link to="/shop"><Button>Zum Shop</Button></Link>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {orders.map((o) => {
              const SM = STATUS_META[o.status] || STATUS_META.received;
              const itemCount = o.items.reduce((s, i) => s + Number(i.qty || 0), 0);
              return (
                <Card key={o.id} className="overflow-hidden" data-testid={`order-${o.id}`}>
                  <div className="px-5 py-4 flex items-start gap-4 border-b border-zinc-100 bg-zinc-50/40">
                    <div className="font-mono text-sm text-zinc-500 shrink-0">{o.order_number}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold tracking-tight">
                        {itemCount} Artikel · {fmtEuro(o.total_cents)}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {new Date(o.created_at).toLocaleString("de-DE", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <span className={cn("inline-flex items-center gap-1 text-[11px] font-bold border rounded-full px-2.5 py-1 shrink-0", SM.cls)}>
                      <SM.Icon className="h-3 w-3" /> {SM.label}
                    </span>
                  </div>
                  <ul className="divide-y divide-zinc-100">
                    {o.items.map((it, idx) => (
                      <li key={idx} className="px-5 py-3 flex items-center gap-3 text-sm">
                        <span className="font-mono text-zinc-400 w-8 text-right">{it.qty}×</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{it.name}</div>
                          {(it.size || it.color) && (
                            <div className="text-[11px] text-zinc-500">{[it.size && `Größe ${it.size}`, it.color].filter(Boolean).join(" · ")}</div>
                          )}
                        </div>
                        <div className="font-mono tabular-nums shrink-0">{fmtEuro(it.line_total_cents)}</div>
                      </li>
                    ))}
                  </ul>
                  <div className="px-5 py-3 grid sm:grid-cols-3 gap-4 text-[12px] border-t border-zinc-100 bg-zinc-50/40">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1">Lieferadresse</div>
                      <div className="leading-snug">
                        {o.shipping_name}<br />
                        {o.shipping_company && <>{o.shipping_company}<br /></>}
                        {o.shipping_address_line}<br />
                        {o.shipping_postal_code} {o.shipping_city}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1">Kontakt</div>
                      <div className="leading-snug truncate">{o.contact_email}<br />{o.contact_phone}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1">Summen</div>
                      <dl className="space-y-0.5">
                        <div className="flex justify-between"><dt className="text-zinc-500">Zwischensumme</dt><dd className="tabular-nums font-mono">{fmtEuro(o.subtotal_cents)}</dd></div>
                        <div className="flex justify-between"><dt className="text-zinc-500">Versand</dt><dd className="tabular-nums font-mono">{o.shipping_cents === 0 ? "Gratis" : fmtEuro(o.shipping_cents)}</dd></div>
                        <div className="flex justify-between font-display font-bold border-t border-zinc-200 pt-1 mt-1"><dt>Gesamt netto</dt><dd className="tabular-nums font-mono">{fmtEuro(o.total_cents)}</dd></div>
                      </dl>
                    </div>
                  </div>
                  {o.notes && (
                    <div className="px-5 py-3 border-t border-zinc-100 text-[12px]">
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Notiz: </span>
                      <span className="text-zinc-700">{o.notes}</span>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
